<template>
  <div
    class="dropdown-wrapper z-20 min-w-[300px] rounded-md bg-white text-sm text-gray-500 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
  >
    <div class="m-4 space-y-4">
      <h2 class="font-bold">
        {{ `${$t("COMMON.IMPERSONATE")} ${$t("COMMON.USER")}` }}
      </h2>
      <lf-dropdown-with-api-search
        :value="activeClient"
        :placeholder="$t('DEALS.ORGANIZATION')"
        :search-term="$t('DEALS.ORGANIZATION')"
        :get-paginated-options="getApprovedClients"
        name="client_id"
        data-cy="organization-dropdown"
        accessor="name"
        full-width
        drop-up
        sort
        @change="changeClient"
      />
      <lf-dropdown-with-api-search
        ref="userDropdown"
        :value="activeUser?.id"
        :placeholder="$t('COMMON.USER')"
        :search-term="$t('COMMON.USER')"
        :get-paginated-options="getUsers"
        :disabled="isEmpty(activeClient)"
        name="user_id"
        data-cy="user-dropdown"
        accessor="name"
        full-width
        drop-up
        @change="changeUser"
      >
        <template #option="{ options, optionId }">
          <span>
            <icon-base
              height="40"
              width="40"
              view-box="0 0 50 50"
              icon="profile"
              class="ml-3 text-grey-icon"
            />
          </span>
          <span
            class="ml-3 flex cursor-pointer flex-col justify-between overflow-hidden font-normal capitalize"
          >
            <span class="truncate font-bold" v-tooltipable>
              {{ getUserName(options, optionId) }}
            </span>
            <span class="truncate" v-tooltipable>
              {{ getUserRole(options, optionId) }}
            </span>
          </span>
        </template>
      </lf-dropdown-with-api-search>
    </div>
    <div class="m-4">
      <primary-button
        :disabled="disabledButton"
        type="button"
        data-cy="impersonate-button"
        class="w-full"
        @click="impersonate"
      >
        {{ $t("COMMON.IMPERSONATE") }}
      </primary-button>
    </div>
  </div>
</template>

<script setup lang="ts" generic="MyUser extends IUser & { name: string }">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";
import type { IClient } from "@/models/clients";
import { getApprovedClients } from "@/services/modules/clients";
import usersApiService from "@/services/modules/users";
import type { IUser } from "@/models/users";
import { formatUserFullName, getUserRoles } from "@/helpers/formatting";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";
import { usePromiseWrapper } from "@/hooks/common";
import { useImpersonate } from "@/hooks/auth";

import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";

const emit = defineEmits<{
  impersonated: [];
}>();

const { dispatch, commit } = useStore();

const { alreadyImpersonatingSomeone } = useImpersonate();

const activeClient = ref();
const activeUser = ref();
const userDropdown = ref<InstanceType<typeof LfDropdownWithApiSearch> | null>(
  null
);

const disabledButton = computed(
  () => isEmpty(activeClient.value) || isEmpty(activeUser.value)
);

const changeClient = (client: IClient) => {
  activeClient.value = client.id;
  userDropdown.value?.resetAll();
  activeUser.value = null;
};

const changeUser = (user: IUser) => {
  activeUser.value = user;
};

const getUsers = async (params: Record<string, unknown>) => {
  const users = await usersApiService.getClientUsers(
    activeClient.value,
    params
  );
  users.data = users.data.map((user: IUser) => ({
    ...user,
    name: formatUserFullName(user)
  }));

  return users;
};

const getUserName = (options: unknown[], optionId: IUser["id"]) => {
  return getUser(options as IUser[], optionId)?.name;
};

const getUserRole = (options: unknown[], optionId: IUser["id"]) => {
  const user = getUser(options as IUser[], optionId);
  return getUserRoles(user.roles)?.join(", ");
};

const getUser = (options: IUser[], optionId: IUser["id"]) => {
  return options.find(({ id }) => id === Number(optionId)) as MyUser;
};

const { fetchWrapper: impersonate } = usePromiseWrapper(async () => {
  if (alreadyImpersonatingSomeone.value) {
    return;
  }
  commit("setPreventGlobalMessage", true);
  await dispatch("auth/impersonateUser", {
    userId: activeUser.value?.id,
    canRedirect: false
  });
  commit("resetHistoryStack", null, { root: true });

  emit("impersonated");
  activeClient.value = null;
  activeUser.value = null;
});
</script>
